// ViewerHeader.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, SvgIcon, Typography } from "@mui/material";
import logo from "../assets/logo.png";
import AttributionBox from "./AttributionBox";
import { hasCreditsContent } from "./credits/CreditsContent";
import CreditsPanel from "./credits/CreditsPanel";
import LanguagePicker from "./LanguagePicker";
import { useLocalization } from "../localization/LocalizationContext";
import {
  getViewerPanelPaddingSx,
  viewerPanelEyebrowSx,
  viewerPanelSurfaceSx,
  viewerPanelTitleSx,
  viewerSubtleIconButtonSx,
} from "./unityWebViewer/panelStyles";

const InformationIcon = (props) => (
  <SvgIcon viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
    <path d="M11 17h2v-6h-2v6Zm1-15C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8Zm-1-11h2V7h-2v2Z" />
  </SvgIcon>
);

const ViewerHeader = ({ system, experienceName = "" }) => {
  const { dir, t } = useLocalization();
  const headerRef = useRef(null);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState(null);
  const informationOpen = activePanel === "information";
  const informationButtonRef = useRef(null);
  const informationPanelsRef = useRef(null);
  const informationPanelId = "viewer-experience-information-panel";
  const creditsPanelId = "viewer-credits-panel";
  const creditsAvailable = hasCreditsContent(system?.credits);
  const showCreditsPanel = creditsAvailable && creditsOpen;
  const displayName = experienceName || system?.name || "";

  useEffect(() => {
    const headerElement = headerRef.current;
    if (!headerElement) return undefined;

    const updateHeaderHeight = () => {
      if (!headerElement.isConnected) return;

      const height = headerElement.getBoundingClientRect().height;
      document.documentElement.style.setProperty(
        "--app-header-height",
        `${Math.ceil(height)}px`
      );
    };

    updateHeaderHeight();

    const ResizeObserverConstructor =
      typeof window !== "undefined" ? window.ResizeObserver : undefined;

    if (!ResizeObserverConstructor) {
      window.addEventListener("resize", updateHeaderHeight);

      return () => {
        window.removeEventListener("resize", updateHeaderHeight);
        document.documentElement.style.removeProperty("--app-header-height");
      };
    }

    const observer = new ResizeObserverConstructor(updateHeaderHeight);
    observer.observe(headerElement);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty("--app-header-height");
    };
  }, []);

  useEffect(() => {
    if (!informationOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!informationButtonRef.current?.contains(event.target) &&
          !informationPanelsRef.current?.contains(event.target)) {
        setActivePanel(null);
        setCreditsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePanel(null);
        setCreditsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown, true);
    document.addEventListener("touchstart", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown, true);
      document.removeEventListener("touchstart", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [informationOpen]);

  const toggleInformationPanel = (event) => {
    event.stopPropagation();
    const nextOpen = !informationOpen;
    setActivePanel(nextOpen ? "information" : null);
    if (!nextOpen) {
      setCreditsOpen(false);
    }
  };

  const openCredits = () => {
    setCreditsOpen(true);
  };

  return (
    <Box>
      <Box
        className="viewer-header"
        ref={headerRef}
        sx={{
          position: "absolute",
          top: 0,
          left: "env(safe-area-inset-left, 0px)",
          right: "env(safe-area-inset-right, 0px)",
          width: "auto",
          color: "#fff",
          px: "var(--viewer-header-px, 16px)",
          py: "var(--viewer-header-py, 10px)",
          zIndex: "var(--start-overlay-chrome-z-index, 2010)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-start",
          alignItems: "center",
          pointerEvents: "none",
        }}
      >
        <Box
          className="viewer-header__brand"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "var(--viewer-header-gap, 12px)",
            position: "relative",
            ...viewerPanelSurfaceSx,
            ...getViewerPanelPaddingSx(false),
            minWidth: 0,
            boxSizing: "border-box",
            maxWidth: "min(92vw, 860px)",
            pointerEvents: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img src={logo} alt="Logo" className="viewer-logo" />
          </Box>
          {displayName && (
            <Box sx={{ minWidth: 0, textAlign: dir === "rtl" ? "right" : "left", flex: "1 1 auto" }}>
              <Typography
                variant="overline"
                sx={{
                  ...viewerPanelEyebrowSx,
                  fontSynthesis: "none",
                }}
              >
                {t("viewer.header.eyebrow")}
              </Typography>
              <Typography
                component="h2"
                sx={{
                  ...viewerPanelTitleSx,
                  mt: 0,
                  lineHeight: 1.15,
                  fontSize: "clamp(14px, 1.35vw, 18px)",
                  fontSynthesis: "none",
                  maxWidth: "min(58vw, 720px)",
                }}
              >
                {displayName}
              </Typography>
            </Box>
          )}
          <IconButton
            ref={informationButtonRef}
            aria-label={t("viewer.info.open")}
            aria-expanded={informationOpen}
            aria-controls={informationOpen ? informationPanelId : undefined}
            onClick={toggleInformationPanel}
            size="small"
            sx={{
              ...viewerSubtleIconButtonSx,
              flexShrink: 0,
              color: informationOpen
                ? "rgba(242, 255, 252, 0.92)"
                : viewerSubtleIconButtonSx.color,
              background: informationOpen
                ? "rgba(210, 255, 249, 0.11)"
                : viewerSubtleIconButtonSx.background,
              borderColor: informationOpen
                ? "rgba(210, 255, 249, 0.22)"
                : "rgba(210, 255, 249, 0.1)",
              opacity: informationOpen ? 1 : viewerSubtleIconButtonSx.opacity,
              "& .MuiSvgIcon-root": {
                fontSize: 19,
              },
            }}
          >
            <InformationIcon />
          </IconButton>
          <LanguagePicker
            confirmReload
            open={activePanel === "language"}
            onOpen={() => {
              setActivePanel("language");
              setCreditsOpen(false);
            }}
            onClose={() => setActivePanel((panel) => panel === "language" ? null : panel)}
          />
          {informationOpen && (
            <Box
              ref={informationPanelsRef}
              data-testid="viewer-information-panels"
              sx={{
                position: "absolute",
                top: "calc(100% + 10px)",
                left: 0,
                width: showCreditsPanel
                  ? "min(965px, calc(100vw - 32px))"
                  : creditsAvailable
                    ? "min(425px, calc(100vw - 32px))"
                    : "min(320px, calc(100vw - 32px))",
                display: "grid",
                gridTemplateColumns: showCreditsPanel
                  ? "minmax(0, 425px) minmax(300px, 1fr)"
                  : "minmax(0, 1fr)",
                alignItems: "start",
                gap: 1.5,
                zIndex: "var(--start-overlay-chrome-z-index, 2010)",
                pointerEvents: "auto",
                "@media (max-width: 700px)": {
                  gridTemplateColumns: "minmax(0, min(425px, 100%))",
                },
              }}
            >
              <AttributionBox
                creditsPanelId={creditsPanelId}
                creditsOpen={showCreditsPanel}
                id={informationPanelId}
                onOpenCredits={openCredits}
                open={informationOpen}
                system={system}
              />
              <CreditsPanel
                id={creditsPanelId}
                onClose={() => setCreditsOpen(false)}
                open={showCreditsPanel}
                system={system}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ViewerHeader;
